import {
	Typography,
	Skeleton,
	Button,
	Dialog,
	List,
	ListItem,
	ListItemButton,
	ListItemText,
	useTheme
} from '@mui/material';
import {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import Card from '../card/Card';
import {Coordinates, CalculationMethod, PrayerTimes} from 'adhan';
import SearchInput from '../search/SearchInput';

//TODO fix import utils
import type {TStates, TCity} from '@constants/cities';
// import {stateCities} from '@constants/cities';
import {AppConstants} from '@constants';

interface IProps {
	date?: Date;
}

interface ICity {
	id: string;
	name: string;
	coordinate: Coordinates;
}

const cities: ICity[] = AppConstants.allStates.map((state: TStates) => {
	return state.cities.map((city: TCity) => {
		return {
			...city,
			id: state.name + city.id
		};
	});
}).flat(1);


export default function AzanBox({date: d}: IProps) {
	const {t} = useTranslation();
	const defaultId = Number(localStorage.getItem('azan_city_id'));
	const defaultCity = cities[typeof defaultId !== 'undefined' && defaultId > 0 ? defaultId : 444];
	// const defaultCity = cities[isNaN(defaultId) ? 0 : defaultId];
	const [open, setOpen] = useState(false);
	const [data, setData] = useState<PrayerTimes>();
	const [city, setCity] = useState<ICity>(defaultCity);
	const [selected, setSelected] = useState(defaultCity);
	const [text, setText] = useState('');
	const theme = useTheme();
	// @ts-ignore
	const btnBg = `linear-gradient(267.1deg, ${theme.palette.primary[400]} 2.03%, ${theme.palette.primary[600]} 100%)`;
	
	
	useEffect(() => {
		const params = CalculationMethod.Tehran();
		const date = d ?? new Date();
		const prayerTimes = new PrayerTimes(city.coordinate, date, params);
		setData(prayerTimes);
	}, [d, city]);
	
	const openChangeCityDialog = () => {
		setOpen(true);
	};
	
	const closeChangeCityDialog = () => {
		setOpen(false);
	};
	
	const handleChangeCity = () => {
		setCity(selected);
		localStorage.setItem('azan_city_id', `${cities.findIndex((c) => c.name === selected.name)}`);
		closeChangeCityDialog();
	};
	
	const handleChangeText = (e: React.ChangeEvent<HTMLInputElement>) => {
		setText(e.target.value);
	};
	
	return (
		<Card className="mx-4" title={t('azan_title', {city: city.name})}
		      action={<Button onClick={openChangeCityDialog}>{t('change_city')}</Button>}>
			<div className="px-2 py-2 flex flex-row justify-between">
				<div className="text-center mx-2">
					<Typography sx={{
						color: (theme) => theme.palette.mode === 'light' ? 'grey.500' : 'grey.300',
						fontSize: (theme) => theme.typography.fontSize - 2
					}}>{t('fajr')}</Typography>
					<Typography className="font-medium" sx={{
						color: (theme) => theme.palette.mode === 'light' ? 'grey.800' : 'grey.200',
						fontSize: (theme) => theme.typography.fontSize - 2
					}}>{data?.fajr.toLocaleTimeString('fa-IR', {hour: '2-digit', minute: '2-digit'}) ?? <Skeleton/>}</Typography>
				</div>
				
				<div className="text-center">
					<Typography sx={{
						color: (theme) => theme.palette.mode === 'light' ? 'grey.500' : 'grey.300',
						fontSize: (theme) => theme.typography.fontSize - 2
					}}>{t('sunrise')}</Typography>
					<Typography className="font-medium" sx={{
						color: (theme) => theme.palette.mode === 'light' ? 'grey.800' : 'grey.200',
						fontSize: (theme) => theme.typography.fontSize - 2
					}}>{data?.sunrise.toLocaleTimeString('fa-IR', {hour: '2-digit', minute: '2-digit'}) ??
						<Skeleton/>}</Typography>
				</div>
				
				<div className="text-center">
					<Typography sx={{
						color: (theme) => theme.palette.mode === 'light' ? 'grey.500' : 'grey.300',
						fontSize: (theme) => theme.typography.fontSize - 2
					}}>{t('dhuhr')}</Typography>
					<Typography className="font-medium" sx={{
						color: (theme) => theme.palette.mode === 'light' ? 'grey.800' : 'grey.200',
						fontSize: (theme) => theme.typography.fontSize - 2
					}}>{data?.dhuhr.toLocaleTimeString('fa-IR', {hour: '2-digit', minute: '2-digit'}) ?? <Skeleton/>}</Typography>
				</div>
				
				<div className="text-center">
					<Typography sx={{
						color: (theme) => theme.palette.mode === 'light' ? 'grey.500' : 'grey.300',
						fontSize: (theme) => theme.typography.fontSize - 2
					}}>{t('sunset')}</Typography>
					<Typography className="font-medium" sx={{
						color: (theme) => theme.palette.mode === 'light' ? 'grey.800' : 'grey.200',
						fontSize: (theme) => theme.typography.fontSize - 2
					}}>{data?.sunset.toLocaleTimeString('fa-IR', {hour: '2-digit', minute: '2-digit'}) ??
						<Skeleton/>}</Typography>
				</div>
				
				<div className="text-center">
					<Typography sx={{
						color: (theme) => theme.palette.mode === 'light' ? 'grey.500' : 'grey.300',
						fontSize: (theme) => theme.typography.fontSize - 2
					}}>{t('maghrib')}</Typography>
					<Typography className="font-medium" sx={{
						color: (theme) => theme.palette.mode === 'light' ? 'grey.800' : 'grey.200',
						fontSize: (theme) => theme.typography.fontSize - 2
					}}>{data?.maghrib.toLocaleTimeString('fa-IR', {hour: '2-digit', minute: '2-digit'}) ??
						<Skeleton/>}</Typography>
				</div>
			</div>
			<Dialog onClose={closeChangeCityDialog} open={open}>
				<div className="p-2 flex flex-col space-y-2">
					<SearchInput placeholder={t('change_city')} onChange={handleChangeText}/>
					<List className="max-h-80 overflow-scroll">
						{
							cities.filter(c => c.name.includes(text)).map(c => <ListItem
								className="rounded-lg mb-1"
								sx={{
									borderColor: c.name === selected.name ? 'secondary.200' : '',
									borderWidth: theme.palette.mode === 'light' && c.name === selected.name ? 1 : 0
								}}
								key={c.id}
								disablePadding>
								<ListItemButton
									onClick={() => setSelected(c)}
									sx={{
										bgcolor: (theme) => c.name === city.name ? (theme.palette.mode === 'light' ? 'secondary.50' : '#272931') : ''
									}}
									className="rounded-lg h-9">
									<ListItemText primary={c.name}/>
								</ListItemButton>
							</ListItem>)
						}
					</List>
					<Button
						style={{background: city.name !== selected.name ? btnBg : ''}}
						disabled={city.name === selected.name}
						variant="contained"
						onClick={handleChangeCity}>
						{t('change_city')}
					</Button>
				</div>
			</Dialog>
		</Card>
	);
}
